import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { Locale, TranslatableKey } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';
import {
    LAST_DAY_OF_THE_MONTH,
    MonthlyIntervals,
    NUMBER_OF_DAYS_IN_MONTH,
    QUARTERLY,
    QuarterlyPeriods,
    TimeOffDateFormatDashes,
} from '@time-off-v3/meta/time-off-meta';
import moment from 'moment';

@Component({
    selector: 'app-quarterly-fields',
    templateUrl: './quarterly-fields.template.html',
    styleUrls: ['./quarterly-fields.style.scss'],
})
export class QuarterlyFieldsComponent implements OnDestroy {
    @Input() timeOffPolicy: TimeOffPolicy;
    @Input() containerClass = 'app-grid';
    @Input() fieldClass = 'columns-5 small-columns-12';
    @Input() firstDayOfPeriodLabel: TranslatableKey = 'time-off-v3.firstDayOfPeriod';

    @Output() requestPreviewParams: EventEmitter<string> = new EventEmitter();

    quarterlyPeriods = QuarterlyPeriods;
    quarterlyDays = MonthlyIntervals;
    quarterlyPeriodSelection = '';

    locale = this.translateService.currentLang as Locale;

    constructor(private translateService: TranslateService) {}

    getQuarterlyPeriods(): string | void {
        if (!this.timeOffPolicy.dayOfYearThree && !this.timeOffPolicy.dayOfYearFour) {
            return;
        }

        const dayOfYearOne = moment(this.timeOffPolicy.dayOfYearOne, TimeOffDateFormatDashes).month();
        const dayOfYearTwo = moment(this.timeOffPolicy.dayOfYearTwo, TimeOffDateFormatDashes).month();
        const dayOfYearThree = moment(this.timeOffPolicy.dayOfYearThree, TimeOffDateFormatDashes).month();
        const dayOfYearFour = moment(this.timeOffPolicy.dayOfYearFour, TimeOffDateFormatDashes).month();

        const period = this.quarterlyPeriods.find((period) => {
            return (
                dayOfYearOne === period.firstQuarter &&
                dayOfYearTwo === period.secondQuarter &&
                dayOfYearThree === period.thirdQuarter &&
                dayOfYearFour === period.fourthQuarter
            );
        });

        if (!period) {
            return;
        }

        // The English label is used as a simple way to ID the selection, it is just used for equality checks below in setQuarterlyPeriods
        // However, the user will only be displayed the string in their own language
        return period.label.en_CA;
    }

    setQuarterlyPeriods(day: number): void {
        // Get the period which is selected from matching it's label
        const selectedPeriod = QuarterlyPeriods.find((period) => this.quarterlyPeriodSelection === period.label.en_CA);

        // Set the four dates on the model
        this.timeOffPolicy.dayOfYearOne = this.transformToDateString(day, selectedPeriod.firstQuarter);
        this.timeOffPolicy.dayOfYearTwo = this.transformToDateString(day, selectedPeriod.secondQuarter);
        this.timeOffPolicy.dayOfYearThree = this.transformToDateString(day, selectedPeriod.thirdQuarter);
        this.timeOffPolicy.dayOfYearFour = this.transformToDateString(day, selectedPeriod.fourthQuarter);
    }

    transformToDateString(day: number, quarter: number): string {
        // Need to get the last day of the month dynamically since it's not the same for each month.
        if (day === LAST_DAY_OF_THE_MONTH) {
            return moment().endOf('month').month(quarter).format(TimeOffDateFormatDashes);
        }

        // Format the dates to what the backend is expecting.
        return moment().date(day).month(quarter).format(TimeOffDateFormatDashes);
    }

    getDayForQuarterlyPeriods(): number | void {
        // Using the dayOfYearThree because it should always be set for a quarterly accrual
        if (this.timeOffPolicy.dayOfYearThree) {
            const date: number = moment(this.timeOffPolicy.dayOfYearThree, TimeOffDateFormatDashes).date();
            return date > NUMBER_OF_DAYS_IN_MONTH ? LAST_DAY_OF_THE_MONTH : date;
        }
    }

    previewDataChanged(): void {
        this.requestPreviewParams.emit(QUARTERLY);
    }

    ngOnDestroy(): void {
        this.timeOffPolicy.resetDateValues(this.timeOffPolicy);
    }
}
